// =============================================
// NoteEditor — TipTap rich-text editor (the Word/OneNote-style note body)
// =============================================
// Stores a STRUCTURED JSON document (not raw HTML) — the secure foundation for a
// multi-user notes service: no stored-XSS surface. Images & videos are uploaded to
// the validated /api/kv/media endpoint and embedded as notesMedia nodes that
// carry only a stable media id (resolved to a live src at render). Reports the JSON
// doc + flattened plain text up to the host (which debounces + autosaves).
// =============================================
import React, { useCallback, useRef, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import {
  FiBold, FiItalic, FiList, FiCode, FiLink, FiRotateCcw, FiRotateCw, FiImage, FiVideo, FiEdit3, FiFilm, FiCalendar,
} from 'react-icons/fi';
import { LuHeading1, LuHeading2, LuListOrdered, LuQuote } from 'react-icons/lu';
import { flattenTipTap } from './notesLib';
import { NotesMedia } from './MediaNode';
import { NotesEmbed } from './EmbedNode';
import { parseEmbed } from './embedUtils';
import { uploadMedia, uploadDrawing, kindForMime, ACCEPTED_MIME } from './notesMedia';
import DrawingPad, { Scene } from './DrawingPad';

interface NoteEditorProps {
  /** The note these media uploads attach to (ownership-checked server-side). */
  noteId: number;
  /** Initial TipTap JSON document. */
  initialContent: any;
  /** Fired (every change) with the current JSON doc + flattened text. */
  onChange: (doc: any, text: string) => void;
  editable?: boolean;
}

interface BtnProps { onClick: () => void; active?: boolean; title: string; children: React.ReactNode; disabled?: boolean; }
const Btn: React.FC<BtnProps> = ({ onClick, active, title, children, disabled }) => (
  <button
    type="button"
    className={`note-tb-btn ${active ? 'active' : ''}`}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={title}
    aria-label={title}
    disabled={disabled}
  >
    {children}
  </button>
);

const Toolbar: React.FC<{ editor: Editor; onAddMedia: (kind: 'image' | 'video') => void; onDraw: () => void; onEmbed: () => void }> = ({ editor, onAddMedia, onDraw, onEmbed }) => {
  const setLink = useCallback(() => {
    const prev = editor.getAttributes('link').href as string | undefined;
    // eslint-disable-next-line no-alert
    const url = window.prompt('Link URL (leave blank to remove):', prev || 'https://');
    if (url === null) return; // cancelled
    if (url.trim() === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  }, [editor]);

  return (
    <div className="note-toolbar">
      <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><FiBold size={15} /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><FiItalic size={15} /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline code"><FiCode size={15} /></Btn>
      <span className="note-tb-sep" />
      <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1"><LuHeading1 size={16} /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2"><LuHeading2 size={16} /></Btn>
      <Btn
        onClick={() => {
          const d = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
          editor.chain().focus().insertContent([
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: d }] },
            { type: 'paragraph' },
          ]).run();
        }}
        title="Insert today's date as a heading"
      ><FiCalendar size={15} /></Btn>
      <span className="note-tb-sep" />
      <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list"><FiList size={15} /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list"><LuListOrdered size={16} /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote"><LuQuote size={15} /></Btn>
      <Btn onClick={setLink} active={editor.isActive('link')} title="Link"><FiLink size={15} /></Btn>
      <span className="note-tb-sep" />
      <Btn onClick={() => onAddMedia('image')} title="Insert image"><FiImage size={15} /></Btn>
      <Btn onClick={() => onAddMedia('video')} title="Insert video"><FiVideo size={15} /></Btn>
      <Btn onClick={onDraw} title="Draw a sketch"><FiEdit3 size={15} /></Btn>
      <Btn onClick={onEmbed} title="Embed a YouTube / Vimeo video"><FiFilm size={15} /></Btn>
      <span className="note-tb-sep" />
      <Btn onClick={() => editor.chain().focus().undo().run()} title="Undo" disabled={!editor.can().undo()}><FiRotateCcw size={15} /></Btn>
      <Btn onClick={() => editor.chain().focus().redo().run()} title="Redo" disabled={!editor.can().redo()}><FiRotateCw size={15} /></Btn>
    </div>
  );
};

const NoteEditor: React.FC<NoteEditorProps> = ({ noteId, initialContent, onChange, editable = true }) => {
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [drawingOpen, setDrawingOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // Stable ref the ProseMirror drop/paste handlers call (they're created once).
  const actionRef = useRef<((file: File, pos?: number) => Promise<void>) | undefined>(undefined);

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Start writing… one line or many paragraphs. Drop or paste an image/video too.' }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' } }),
      NotesMedia,
      NotesEmbed,
    ],
    content: initialContent,
    editorProps: {
      handleDrop: (view, event) => {
        const files = Array.from((event as DragEvent).dataTransfer?.files || []).filter(f => kindForMime(f.type));
        if (!files.length) return false;
        event.preventDefault();
        const coords = view.posAtCoords({ left: (event as DragEvent).clientX, top: (event as DragEvent).clientY });
        let pos = coords?.pos;
        void (async () => { for (const f of files) { await actionRef.current?.(f, pos); pos = undefined; } })();
        return true;
      },
      handlePaste: (_view, event) => {
        const files = Array.from((event as ClipboardEvent).clipboardData?.files || []).filter(f => kindForMime(f.type));
        if (!files.length) return false;
        event.preventDefault();
        void (async () => { for (const f of files) { await actionRef.current?.(f); } })();
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const doc = ed.getJSON();
      onChange(doc, flattenTipTap(doc));
    },
  }, [editable]);

  // Upload a file and insert a media node (at `pos` if given, else at the cursor).
  const uploadAndInsert = useCallback(async (file: File, pos?: number) => {
    if (!editor) return;
    if (!kindForMime(file.type)) { setMsg('Only images and videos can be embedded'); return; }
    setUploading(true); setMsg(null);
    try {
      const m = await uploadMedia(noteId, file);
      const attrs = { mediaId: m.media_id, kind: m.kind, mime: m.mime, name: m.original_name || file.name };
      if (typeof pos === 'number') editor.chain().focus().insertContentAt(pos, { type: 'notesMedia', attrs }).run();
      else editor.chain().focus().insertContent({ type: 'notesMedia', attrs }).run();
    } catch (e: any) {
      setMsg(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [editor, noteId]);
  actionRef.current = uploadAndInsert;

  const openPicker = useCallback((kind: 'image' | 'video') => {
    const el = fileInput.current;
    if (!el) return;
    el.accept = kind === 'image' ? 'image/png,image/jpeg,image/gif,image/webp' : 'video/mp4,video/webm,video/ogg,video/quicktime';
    el.click();
  }, []);

  const onFilesPicked = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file
    for (const f of files) await uploadAndInsert(f);
  }, [uploadAndInsert]);

  const insertEmbed = useCallback(() => {
    if (!editor) return;
    // eslint-disable-next-line no-alert
    const url = window.prompt('Paste a YouTube or Vimeo link:');
    if (!url) return;
    const e = parseEmbed(url);
    if (!e) { setMsg('Only YouTube and Vimeo links can be embedded'); return; }
    setMsg(null);
    editor.chain().focus().insertContent({ type: 'notesEmbed', attrs: { provider: e.provider, videoId: e.videoId } }).run();
  }, [editor]);

  const insertDrawing = useCallback(async (scene: Scene, png: Blob) => {
    if (!editor) return;
    setUploading(true); setMsg(null);
    try {
      const m = await uploadDrawing(noteId, scene, png);
      editor.chain().focus().insertContent({
        type: 'notesMedia', attrs: { mediaId: m.media_id, kind: 'drawing', mime: 'image/png', name: 'Drawing' },
      }).run();
      setDrawingOpen(false);
    } catch (e: any) {
      setMsg(e?.message || 'Could not save drawing');
    } finally {
      setUploading(false);
    }
  }, [editor, noteId]);

  if (!editor) return <div className="note-editor-loading">Loading editor…</div>;

  return (
    <div className="note-editor">
      <Toolbar editor={editor} onAddMedia={openPicker} onDraw={() => setDrawingOpen(true)} onEmbed={insertEmbed} />
      {(uploading || msg) && (
        <div className={`note-upload-bar ${msg ? 'err' : ''}`}>
          {uploading ? 'Uploading…' : msg}
        </div>
      )}
      <EditorContent editor={editor} className="note-editor-content" />
      <input
        ref={fileInput}
        type="file"
        accept={ACCEPTED_MIME}
        multiple
        style={{ display: 'none' }}
        onChange={onFilesPicked}
      />
      {drawingOpen && (
        <DrawingPad
          title="New drawing"
          onSave={insertDrawing}
          onCancel={() => setDrawingOpen(false)}
        />
      )}
    </div>
  );
};

export default NoteEditor;
